/**
 * Publish Modal — owner-side UI to publish a Knod as a public-facing form.
 *
 * Opened from the Knod actions menu in the editor toolbar. Lets the user:
 *   - Validate the flow has detectable inputs and outputs (form preview)
 *   - Auto-add missing inputs/outputs (uses inspectFlowForPublish on the live state)
 *   - Publish (owner-only — visitors must be signed into the owner's account)
 *   - Rename / disable / revoke
 *
 * Multi-tenant access (sharing with specific other accounts) is planned but
 * not yet built — until then, only the owner can open the published URL.
 *
 * Styled with the project's design system tokens (see styles/themes/_variables.css).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Switch,
  Tooltip,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import OutputIcon from '@mui/icons-material/Output';
import {
  PublishRow,
  getPublishByFlow,
  createPublish,
  updatePublish,
  deletePublish,
  buildPublishUrl,
} from '../services/PublishService';
import { toastService } from './ui/Toast';
import type { InspectionResult } from '../utils/publishSchemaInspector';
import './PublishModal.css';

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  flowId: string | null;
  flowName?: string;
  /** Inspect the LIVE in-editor state — returns inputs/outputs/suggestions. */
  inspect: () => InspectionResult;
  /** Apply auto-add suggestions to live editor state. Returns the new inspection. */
  onAutoAdd: () => Promise<InspectionResult>;
  /** Update an input/output label on the underlying node. Returns fresh inspection. */
  onUpdateLabel: (kind: 'input' | 'output', nodeId: string, label: string) => InspectionResult;
  /** Persist the current flow (used after auto-add and before publishing). */
  onSaveBeforePublish: () => Promise<void>;
}

// Design-system color helpers — referenced via CSS vars in PublishModal.css.
// Inline overrides for MUI components that don't easily accept className-based styling.
const dsPaperSx = {
  backgroundColor: 'var(--color-bg-surface)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  fontFamily: 'var(--font-primary)',
};

const dsInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'var(--input-bg)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-primary)',
    fontSize: 'var(--font-size-sm)',
    '& fieldset': { borderColor: 'var(--input-border)' },
    '&:hover fieldset': { borderColor: 'var(--input-border-hover)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--input-border-focus)' },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-primary)',
    fontSize: 'var(--font-size-sm)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--color-accent-primary)',
  },
  '& .MuiFormHelperText-root': {
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-xs)',
  },
};

function iconForType(type: string) {
  switch (type) {
    case 'image':
      return <ImageIcon sx={{ fontSize: 16, color: 'var(--color-type-image)' }} />;
    case 'video':
      return <VideoFileIcon sx={{ fontSize: 16, color: 'var(--color-type-video)' }} />;
    case 'audio':
      return <AudioFileIcon sx={{ fontSize: 16, color: 'var(--color-type-text)' }} />;
    case 'text':
      return <TextFieldsIcon sx={{ fontSize: 16, color: 'var(--color-type-text)' }} />;
    default:
      return <OutputIcon sx={{ fontSize: 16, color: 'var(--color-type-any)' }} />;
  }
}

const PublishModal: React.FC<PublishModalProps> = ({
  open,
  onClose,
  flowId,
  flowName,
  inspect,
  onAutoAdd,
  onUpdateLabel,
  onSaveBeforePublish,
}) => {
  // `loadingRow` only gates the publish-row fetch (which is fast and only runs
  // when the modal opens). It does NOT blank the panel — we render the form
  // immediately from the synchronous inspection and only show a faint spinner
  // in the action bar if the row fetch is still in flight.
  const [loadingRow, setLoadingRow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoAdding, setAutoAdding] = useState(false);
  const [publish, setPublish] = useState<PublishRow | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Inspection of the live editor state. Refreshed on open and after auto-add.
  const [inspection, setInspection] = useState<InspectionResult | null>(null);

  // Stable ref to the latest inspect() function. Without this, the load effect
  // below would re-fire every time React Flow's `nodes` array gets a new
  // reference (which happens on EVERY label commit), causing the modal to
  // briefly blank out and show a spinner. We only want the load effect to run
  // when the modal opens, not on every editor state change.
  const inspectRef = useRef(inspect);
  useEffect(() => {
    inspectRef.current = inspect;
  }, [inspect]);

  // Label drafts — keyed by `${kind}:${nodeId}`. Local state lets the user
  // type without churning React Flow on every keystroke; commit happens on blur
  // or Enter, then the inspection is refreshed.
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});

  // Per-field "saved" pulse: shows a quick checkmark next to a label after
  // it's committed, so the user gets feedback without the entire panel
  // re-rendering or blanking. Keyed the same way as labelDrafts.
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});

  // Seed drafts only when the SET of input/output node IDs changes (open / auto-add).
  // We deliberately do NOT depend on the full `inspection` object reference, because
  // committing a single label rebuilds it and would clobber the user's typing mid-edit
  // and force every input to re-render (the "blink").
  const ioNodeIdsKey = useMemo(() => {
    if (!inspection) return '';
    const inputs = inspection.inputs.map((i) => i.nodeId).join(',');
    const outputs = inspection.outputs.map((o) => o.nodeId).join(',');
    return `${inputs}|${outputs}`;
  }, [inspection]);

  useEffect(() => {
    if (!inspection) return;
    const drafts: Record<string, string> = {};
    for (const i of inspection.inputs) drafts[`input:${i.nodeId}`] = i.label;
    for (const o of inspection.outputs) drafts[`output:${o.nodeId}`] = o.label;
    setLabelDrafts(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ioNodeIdsKey]);

  const commitLabel = (kind: 'input' | 'output', nodeId: string) => {
    const key = `${kind}:${nodeId}`;
    const draft = labelDrafts[key] ?? '';
    const current =
      kind === 'input'
        ? inspection?.inputs.find((i) => i.nodeId === nodeId)?.label
        : inspection?.outputs.find((o) => o.nodeId === nodeId)?.label;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === current) {
      // No-op (empty or unchanged). Reset to current so the field doesn't show a stale empty value.
      if (current !== undefined) setLabelDrafts((prev) => ({ ...prev, [key]: current }));
      return;
    }
    const next = onUpdateLabel(kind, nodeId, trimmed);
    setInspection(next);

    // Show a brief "saved" check next to this field. Local key-scoped state
    // means only the affected row re-renders.
    setSavedFlash((prev) => ({ ...prev, [key]: true }));
    window.setTimeout(() => {
      setSavedFlash((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }, 1200);
  };

  // Load publish row + inspect the flow when the modal opens. Intentionally
  // depends ONLY on `[open, flowId]` — `inspect` changes on every editor tick,
  // so listing it would re-fire this effect on every label commit and blank
  // the panel. We read inspect from the stable ref instead.
  useEffect(() => {
    if (!open || !flowId) return;
    let cancelled = false;
    setLoadingRow(true);
    // Synchronous inspection — populates the form immediately, no spinner.
    setInspection(inspectRef.current());

    getPublishByFlow(flowId)
      .then((row) => {
        if (cancelled) return;
        setPublish(row);
        if (row) {
          setTitle(row.title || '');
          setDescription(row.description || '');
          setEnabled(row.enabled);
        } else {
          setTitle('');
          setDescription('');
          setEnabled(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PublishModal] load error:', err);
        toastService.error(err.message || 'Failed to load publish');
      })
      .finally(() => {
        if (!cancelled) setLoadingRow(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, flowId]);

  const publicUrl = publish ? buildPublishUrl(publish.slug) : null;

  const validation = useMemo(() => {
    if (!inspection) return null;
    const ok = inspection.hasInputs && inspection.hasOutputs;
    const messages: string[] = [];
    if (!inspection.hasInputs) messages.push('No inputs detected');
    if (!inspection.hasOutputs) messages.push('No outputs detected');
    return { ok, messages };
  }, [inspection]);

  const canAutoFix = !!inspection && inspection.fixableCount > 0 && !validation?.ok;

  const handleAutoAdd = async () => {
    setAutoAdding(true);
    try {
      const next = await onAutoAdd();
      setInspection(next);
    } catch (err: any) {
      console.error('[PublishModal] auto-add error:', err);
      toastService.error(err.message || 'Auto-add failed');
    } finally {
      setAutoAdding(false);
    }
  };

  const handlePublish = async () => {
    if (!flowId) return;
    if (!validation?.ok) {
      toastService.error('Add at least one input and one output before publishing');
      return;
    }
    setSaving(true);
    try {
      // Make sure the latest editor state is in the DB before publishing
      await onSaveBeforePublish();
      const row = await createPublish({
        flowId,
        title: title || undefined,
        description: description || undefined,
      });
      setPublish(row);
      toastService.success('Knod published');
    } catch (err: any) {
      console.error('[PublishModal] create error:', err);
      toastService.error(err.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!publish) return;
    setSaving(true);
    try {
      const row = await updatePublish(publish.id, {
        title: title || null,
        description: description || null,
        enabled,
      });
      setPublish(row);
      toastService.success('Publish updated');
    } catch (err: any) {
      console.error('[PublishModal] update error:', err);
      toastService.error(err.message || 'Failed to update publish');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!publish) return;
    if (!window.confirm('Revoke this publish? The URL will stop working immediately.')) return;
    setSaving(true);
    try {
      await deletePublish(publish.id);
      setPublish(null);
      setTitle('');
      setDescription('');
      setEnabled(true);
      toastService.success('Publish revoked');
    } catch (err: any) {
      console.error('[PublishModal] revoke error:', err);
      toastService.error(err.message || 'Failed to revoke publish');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toastService.success('URL copied');
    } catch {
      toastService.error('Failed to copy');
    }
  };

  const handleOpen = () => {
    if (publicUrl) window.open(publicUrl, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dsPaperSx }}
      className="publish-modal"
    >
      <DialogTitle className="publish-modal__title">
        <PublicIcon sx={{ fontSize: 20, color: 'var(--color-accent-primary)' }} />
        <span>Publish Knod</span>
      </DialogTitle>

      <DialogContent dividers className="publish-modal__content">
        <p className="publish-modal__intro">
          Publishing turns this Knod into a simple form anyone with the URL can fill in.
          They provide the inputs, the flow runs on your account, and they see the outputs.{' '}
          <strong>Credits are charged to you.</strong>
        </p>

            {/* Form preview / validation */}
            {inspection && (
              <section className="publish-modal__section">
                <header className="publish-modal__section-header">
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-2xs)',
                      letterSpacing: '0.08em',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    Form preview
                  </Typography>
                  {validation?.ok ? (
                    <span className="publish-modal__badge publish-modal__badge--ok">
                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                      Ready to publish
                    </span>
                  ) : (
                    <span className="publish-modal__badge publish-modal__badge--warn">
                      <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                      {validation?.messages.join(' · ')}
                    </span>
                  )}
                </header>

                <div className="publish-modal__schema">
                  <div className="publish-modal__schema-col">
                    <div className="publish-modal__schema-label">Inputs</div>
                    {inspection.inputs.length === 0 ? (
                      <div className="publish-modal__schema-empty">None detected</div>
                    ) : (
                      <ul className="publish-modal__schema-list">
                        {inspection.inputs.map((entry) => {
                          const key = `input:${entry.nodeId}`;
                          return (
                            <li key={entry.nodeId}>
                              {iconForType(entry.type)}
                              <input
                                className="publish-modal__schema-input"
                                value={labelDrafts[key] ?? entry.label}
                                onChange={(e) =>
                                  setLabelDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                                }
                                onBlur={() => commitLabel('input', entry.nodeId)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLInputElement).blur();
                                  } else if (e.key === 'Escape') {
                                    setLabelDrafts((prev) => ({ ...prev, [key]: entry.label }));
                                    (e.currentTarget as HTMLInputElement).blur();
                                  }
                                }}
                                aria-label={`Label for input "${entry.label}"`}
                              />
                              {savedFlash[key] && (
                                <CheckCircleIcon
                                  className="publish-modal__saved-check"
                                  sx={{ fontSize: 14, color: 'var(--color-accent-success)' }}
                                />
                              )}
                              {entry.required && (
                                <span className="publish-modal__required">required</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div className="publish-modal__schema-col">
                    <div className="publish-modal__schema-label">Outputs</div>
                    {inspection.outputs.length === 0 ? (
                      <div className="publish-modal__schema-empty">None detected</div>
                    ) : (
                      <ul className="publish-modal__schema-list">
                        {inspection.outputs.map((entry) => {
                          const key = `output:${entry.nodeId}`;
                          return (
                            <li key={entry.nodeId}>
                              {iconForType(entry.type)}
                              <input
                                className="publish-modal__schema-input"
                                value={labelDrafts[key] ?? entry.label}
                                onChange={(e) =>
                                  setLabelDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                                }
                                onBlur={() => commitLabel('output', entry.nodeId)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLInputElement).blur();
                                  } else if (e.key === 'Escape') {
                                    setLabelDrafts((prev) => ({ ...prev, [key]: entry.label }));
                                    (e.currentTarget as HTMLInputElement).blur();
                                  }
                                }}
                                aria-label={`Label for output "${entry.label}"`}
                              />
                              {savedFlash[key] && (
                                <CheckCircleIcon
                                  className="publish-modal__saved-check"
                                  sx={{ fontSize: 14, color: 'var(--color-accent-success)' }}
                                />
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {canAutoFix && (
                  <div className="publish-modal__autofix">
                    <div className="publish-modal__autofix-text">
                      I can auto-add{' '}
                      {inspection.suggestions.sourcesNeedingInput.length > 0 && (
                        <strong>
                          {inspection.suggestions.sourcesNeedingInput.length} input panel
                          {inspection.suggestions.sourcesNeedingInput.length === 1 ? '' : 's'}
                        </strong>
                      )}
                      {inspection.suggestions.sourcesNeedingInput.length > 0 &&
                        inspection.suggestions.sinksNeedingOutput.length > 0 &&
                        ' and '}
                      {inspection.suggestions.sinksNeedingOutput.length > 0 && (
                        <strong>
                          {inspection.suggestions.sinksNeedingOutput.length} output flag
                          {inspection.suggestions.sinksNeedingOutput.length === 1 ? '' : 's'}
                        </strong>
                      )}
                      {' '}for you.
                    </div>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
                      onClick={handleAutoAdd}
                      disabled={autoAdding}
                      className="publish-modal__btn publish-modal__btn--secondary"
                    >
                      {autoAdding ? 'Adding…' : 'Auto-add'}
                    </Button>
                  </div>
                )}
              </section>
            )}

            {/* Public URL row */}
            {publish && publicUrl && (
              <div className="publish-modal__url-row">
                <span className="publish-modal__url" title={publicUrl}>
                  {publicUrl}
                </span>
                <Tooltip title="Copy URL">
                  <IconButton
                    size="small"
                    onClick={handleCopyUrl}
                    sx={{ color: 'var(--color-text-secondary)' }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{ color: 'var(--color-text-secondary)' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </div>
            )}

            {/* Settings */}
            <section className="publish-modal__section">
              <TextField
                label="Display title"
                size="small"
                fullWidth
                value={title}
                placeholder={flowName || 'Untitled Knod'}
                onChange={(e) => setTitle(e.target.value)}
                helperText="Shown to visitors. Defaults to the Knod name."
                sx={dsInputSx}
              />

              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                value={description}
                placeholder="Tell visitors what this Knod does and how to use it…"
                onChange={(e) => setDescription(e.target.value)}
                helperText="Shown under the title on the published page. Optional."
                sx={{ ...dsInputSx, mt: 'var(--spacing-md)' }}
              />

              <div className="publish-modal__access">
                <LockPersonIcon
                  sx={{ fontSize: 18, color: 'var(--color-text-secondary)', mt: '2px' }}
                />
                <div>
                  <div className="publish-modal__access-title">Owner-only access</div>
                  <div className="publish-modal__access-hint">
                    The URL only works when you're signed into your own account.
                    Multi-tenant sharing is coming soon.
                  </div>
                </div>
              </div>

              {publish && (
                <FormControlLabel
                  sx={{ mt: 'var(--spacing-sm)' }}
                  control={
                    <Switch
                      size="small"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--color-accent-primary)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--color-accent-primary)',
                        },
                      }}
                    />
                  }
                  label={
                    <span className="publish-modal__switch-label">
                      {enabled ? 'Enabled — URL is live' : 'Disabled — URL returns 404'}
                    </span>
                  }
                />
              )}
            </section>
      </DialogContent>

      <DialogActions className="publish-modal__actions">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          {publish && (
            <Button
              onClick={handleRevoke}
              disabled={saving}
              className="publish-modal__btn publish-modal__btn--danger"
            >
              Revoke
            </Button>
          )}
          {loadingRow && (
            <CircularProgress
              size={14}
              sx={{ color: 'var(--color-text-secondary)' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
          <Button
            onClick={onClose}
            disabled={saving}
            className="publish-modal__btn publish-modal__btn--ghost"
          >
            Close
          </Button>
          {publish ? (
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={saving}
              className="publish-modal__btn publish-modal__btn--primary"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={saving || !flowId || !validation?.ok}
              className="publish-modal__btn publish-modal__btn--primary"
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PublishModal;
